// FLAGS: none
// The language prefix the app wrote into the address itself, per tab. A visitor's own prefix is a choice and
// stands; the app's is a guess (hostname or fallback) that a later-known profile replaces. Session storage keeps it
// across reloads and sign-in round trips; any script can write there, so only a real prefix is returned. Never
// throws (the boot reads it before setting the basename): blocked storage means "no guess". Also holds the profile
// hook's reload record, its circuit breaker.
import { getLanguagePathPrefix } from "@src/utils/languagePathPrefix";

const KEY = "languagePrefixAddedByApp";

export const rememberAddedLanguagePrefix = (prefix: string): void => {
  try {
    sessionStorage.setItem(KEY, prefix);
  } catch {
    // Blocked storage: the prefix is then treated as the visitor's choice.
  }
};

export const getAddedLanguagePrefix = (): string | undefined => {
  try {
    const value = sessionStorage.getItem(KEY);
    return value !== null && getLanguagePathPrefix(`/${value}`) === value
      ? value
      : undefined;
  } catch {
    return undefined;
  }
};

export const forgetAddedLanguagePrefix = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing was stored.
  }
};

const RELOAD_KEY = "languagePrefixReloadedFor";

// The profile code the profile hook last reloaded for in this tab: it never reloads twice for one code, so if the
// boot ever lands on a different prefix than the hook expected, the cost is one extra load, not a loop.
export const rememberProfileReload = (code: string): void => {
  try {
    sessionStorage.setItem(RELOAD_KEY, code);
  } catch {
    // Blocked storage: the hook's own guards still apply.
  }
};

export const hasReloadedForProfile = (code: string): boolean => {
  try {
    return sessionStorage.getItem(RELOAD_KEY) === code;
  } catch {
    // Unreadable: answer as if it had, so a reload never repeats unchecked.
    return true;
  }
};

export const forgetProfileReload = (): void => {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // Nothing was stored.
  }
};
